import React, { useState } from "react";
import { GetSnapshotEventGroupedResponseItem } from "@/modules/snapshots/businessReports.types";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface OverdueEvent extends GetSnapshotEventGroupedResponseItem {
  eventDate: string;
  documentName: string;
  actionTaker: string;
  collection: string;
  creator: string;
  // [key: string]: number | React.ReactNode | string;
  daysOverdue: number;
}

interface OverdueAlertPanelProps {
  groupedEvents: Record<string, Array<GetSnapshotEventGroupedResponseItem>>;
  getStatusColor: (status: string, isOverdue: boolean) => string;
}

export const OverdueAlertPanel: React.FC<OverdueAlertPanelProps> = ({ groupedEvents }) => {
  const { formatDate } = useDateFormatter();
  const [clickedItem, setClickedItem] = useState<OverdueEvent | null>(null);
  const [tooltipPosition, _setTooltipPosition] = useState({ x: 0, y: 0 });
  const events = Object.values(groupedEvents).flat();

  const overdueEvents = events.filter(event => event.status === "overdue");

  const overdueWithDays = overdueEvents
    .map(event => {
      const eventDate = new Date(event.event_date);
      const today = new Date();
      const daysOverdue = Math.floor((today.getTime() - eventDate.getTime()) / (1000 * 60 * 60 * 24));
      return { ...event, daysOverdue };
    })
    .sort((a, b) => b.daysOverdue - a.daysOverdue);

  const topOverdueItems = overdueWithDays.slice(0, 5);

  const getPriorityLevel = (days: number) => {
    if (days >= 30) return "Critical";
    if (days >= 14) return "High";
    if (days >= 7) return "Medium";
    return "Low";
  };

  const getPriorityColor = (days: number) => {
    if (days >= 30) return "#dc2626";
    if (days >= 14) return "#ea580c";
    if (days >= 7) return "#d97706";
    return "#ef4444";
  };

  const handleTooltipClose = () => setClickedItem(null);

  // Early return if no overdue events
  if (overdueEvents.length === 0) {
    return (
      <div className="flex min-h-[320px] w-full flex-col items-center justify-center rounded-lg border border-[#e9e0d5] bg-white p-5 shadow-sm">
        <div className="mb-3 text-5xl">✅</div>
        <div className="mb-1 text-base font-semibold text-green-500">All Caught Up!</div>
        <div className="text-center text-xs text-muted-foreground">No overdue items at this time</div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[320px] w-full flex-col rounded-lg border border-[#e9e0d5] bg-white p-5 shadow-sm">
      {/* Header */}
      <div className="mb-4 text-center">
        <h3 className="m-0 text-base font-semibold text-[#0a213e]">Overdue Items</h3>
      </div>

      {/* Summary stats */}
      <div className="mb-4 grid grid-cols-2 gap-2">
        <div className="rounded bg-red-50 p-2 text-center">
          <div className="text-lg font-bold text-red-500">{overdueEvents.length}</div>
          <div className="text-[10px] font-medium text-red-900">Total Overdue</div>
        </div>
        <div className="rounded bg-red-50 p-2 text-center">
          <div className="text-lg font-bold" style={{ color: "#dc2626" }}>
            {overdueWithDays.filter(item => item.daysOverdue >= 30).length}
          </div>
          <div className="text-[10px] font-medium text-red-900">Critical (30+ days)</div>
        </div>
      </div>

      {/* Top overdue items list */}
      <div className="flex-1 overflow-hidden">
        <div className="mb-2 text-xs font-semibold text-muted-foreground">Most Overdue:</div>
        <div className="flex max-h-[200px] flex-col gap-1.5 overflow-y-auto">
          {topOverdueItems.map((item, index) => (
            <div
              key={index}
              className="flex cursor-pointer items-center gap-2 rounded border bg-[#fafafa] p-2 transition-all"
              style={{
                borderColor: `${getPriorityColor(item.daysOverdue)}20`,
              }}
              onClick={() => {}}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#fafafa")}
            >
              {/* Priority indicator */}
              <div className="h-7 w-1.5 rounded" style={{ backgroundColor: getPriorityColor(item.daysOverdue) }} />
              {/* Content */}
              <div className="flex-1 overflow-hidden">
                <div className="truncate text-[11px] font-medium text-[#0a213e]">
                  {item.document_name.length > 25 ? item.document_name.substring(0, 25) + "..." : item.document_name}
                </div>
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {/*{item.recipients[0].split("@")[0]} • {item.daysOverdue} days*/}
                </div>
              </div>
              {/* Days overdue badge */}
              <div
                className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold text-white"
                style={{ backgroundColor: getPriorityColor(item.daysOverdue) }}
              >
                {item.daysOverdue}d
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip for clicked item */}
      {clickedItem && (
        <div
          className="fixed z-[1000] min-w-[280px] max-w-[300px] rounded-lg border border-[#e9e0d5] bg-white p-4 text-sm shadow-lg"
          style={{
            left: Math.max(10, tooltipPosition.x),
            top: Math.max(10, tooltipPosition.y),
          }}
        >
          {/* Close button */}
          <div
            className="absolute right-2 top-2 cursor-pointer text-lg font-bold text-muted-foreground"
            onClick={handleTooltipClose}
          >
            ×
          </div>
          {/* Content */}
          <div>
            <div className="mb-3 pr-5 text-base font-semibold text-[#0a213e]">Overdue Item Details</div>
            <div className="grid gap-2">
              <div className="flex flex-col gap-0.5">
                <div className="text-md font-medium text-[#0a213e]">Document:</div>
                <div className="text-xs text-[#3c3c41]">{clickedItem.documentName}</div>
              </div>
              <div className="flex justify-between">
                <span className="text-md font-medium text-[#0a213e]">Due Date:</span>
                <span className="text-[#3c3c41]">{formatDate(clickedItem.eventDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-md font-medium text-[#0a213e]">Days Overdue:</span>
                <span className="font-semibold" style={{ color: getPriorityColor(clickedItem.daysOverdue) }}>
                  {clickedItem.daysOverdue} days
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-md font-medium text-[#0a213e]">Priority:</span>
                <span
                  className="rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                  style={{ backgroundColor: getPriorityColor(clickedItem.daysOverdue) }}
                >
                  {getPriorityLevel(clickedItem.daysOverdue)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-md font-medium text-[#0a213e]">Collection:</span>
                <span className="text-[#3c3c41]">{clickedItem.collection}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-md font-medium text-[#0a213e]">Creator:</span>
                <span className="text-[#3c3c41]">{clickedItem.creator}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-md font-medium text-[#0a213e]">Action Taker:</span>
                <span className="text-[#3c3c41]">{clickedItem.actionTaker}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
